import {
  ActionFunctionArgs,
  Form,
  useLoaderData,
  redirect,
} from "react-router-dom";
import toast from "react-hot-toast";
import IndeterminateCheckbox from "../../components/inputs/IndeterminantCheckbox";
import { Input } from "../../components/inputs/Input";
import CustomSelect from "../../components/customSelect";
import { FormLayout } from "../../layout/FormLayout";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import {
  GET_LIST_EVENTS_BY_ORGANIZATION,
  GET_LIST_VENDORS_BY_ORGANIZATION,
  LOCATIONS_BY_ORGANIZATION,
  QUERY_LIST_ITEMS,
} from "../../queries";
import { GET_IMAGE_LIBRARY_ITEMS_LIMITED } from "../image-library/queries";
import { organizationStore } from "../../store/organization";
import { Item, ImageLibrary } from "../../types/item";
import { Location } from "../../types/location";
import { Vendor } from "../../types/vendor";
import {
  CREATE_LOCATION_MENUS,
  CREATE_MENU,
  CREATE_MENU_ITEMS,
} from "./mutations";
import { usePermissions } from "../auth/api";
import { userStore } from "../../store/user";
import { useEffect, useState } from "react";
import { usdFormat } from "../../utils";
import { useQuery } from "@tanstack/react-query";
import { Breadcrumbs } from "../../components/Breadcrumbs";
import { Event } from "../../types/event";
import {Tab} from "../../components/Tab";
import { CategoryManager, Category, getDefaultCategories, categoryArrayToObject } from "./components/CategoryManager";

type LocationsIds = string;
type ItemsIds = string;
const client = createApolloClient();
const { getState } = organizationStore;
const { getState: getUserState } = userStore;

export const menuCreateLoader = async () => {
  const organizationId = getState().organizationId;
  const user = getUserState().user;

  let vendorWhere = {};
  let locationWhere: any = {};
  let queryWhere: any = {};
  let eventWhere = {};

  locationWhere = {
    ...locationWhere,
    is_active: { _eq: true },
  };

  queryWhere = {
    ...queryWhere,
    is_active: { _eq: true },
  };
  if (user?.vendors) {
    vendorWhere = {
      ...vendorWhere,
      id: {
        _in: user?.vendors,
      },
    };
  }
  if (user?.events) {
    eventWhere = {
      ...eventWhere,
      id: {
        _in: user?.events,
      },
    };
  }
  if (user?.events) {
    locationWhere = {
      ...locationWhere,
      event: {
        id: {
          _in: user?.events,
        },
      },
    };
    queryWhere = {
      ...queryWhere,
      event: {
        id: {
          _in: user?.events,
        },
      },
    };
  }
  if (organizationId) {
    vendorWhere = {
      ...vendorWhere,
      organization_id: {
        _eq: organizationId,
      },
    };

    eventWhere = {
      ...eventWhere,
      organization_id: {
        _eq: organizationId,
      },
    };

    locationWhere = {
      ...locationWhere,
      event: {
        ...(locationWhere?.event || {}),
        organization_id: {
          _eq: organizationId,
        },
      },
    };

    if (user?.vendors) {
      locationWhere = {
        ...locationWhere,
        vendor_id: {
          _in: user?.vendors,
        },
      };
    }

    queryWhere = {
      ...queryWhere,
      event: {
        ...(queryWhere?.event || {}),
        organization_id: {
          _eq: organizationId,
        },
      },
    };
  }

  const [
    { data: itemData },
    { data: vendorData },
    { data: locationData },
    { data: eventsData },
    { data: imageData },
  ] = await Promise.all([
    client.query({
      query: QUERY_LIST_ITEMS,
      variables: {
        where: queryWhere,
        order_by: {
          name: "asc",
        },
      },
    }),
    client.query({
      query: GET_LIST_VENDORS_BY_ORGANIZATION,
      variables: {
        where: vendorWhere,
      },
    }),
    client.query({
      query: LOCATIONS_BY_ORGANIZATION,
      variables: {
        where: locationWhere,
      },
    }),
    client.query({
      query: GET_LIST_EVENTS_BY_ORGANIZATION,
      variables: {
        where: eventWhere,
      },
    }),
    client.query({
      query: GET_IMAGE_LIBRARY_ITEMS_LIMITED,
      variables: {
        where: eventWhere,
      },
    }),
  ]);
  return {
    vendors: vendorData.vendors,
    items: itemData.items,
    locations: locationData.locations,
    events: eventsData.events,
    images: imageData.image_library,
  };
};

export const menuCreateAction = async ({ request }: ActionFunctionArgs) => {
  const permissions = getUserState().permissions;
  const user = getUserState().user;

  const body = await request.formData();
  const locationsIds: LocationsIds[] = body.getAll(
    "locationsIds"
  ) as LocationsIds[];
  const itemsIds: ItemsIds[] = body.getAll("itemsIds") as ItemsIds[];

  body.delete("itemsIds");
  body.delete("locationsIds");

  // Get dynamic category count
  const categoryCount = parseInt(body.get("category_count") as string) || 6;

  const allItemsValue = body.get("all_items");
  let allItemsIds: string[] = [];
  if (typeof allItemsValue === "string") {
    allItemsIds = JSON.parse(allItemsValue);
  }
  const allItemsIdsStringArray = allItemsIds.map((id) => id.toString());
  body.delete("all_items");
  const bodyData: any = Object.fromEntries(body);

  // Build category object dynamically
  const category: any = {};
  for (let i = 1; i <= categoryCount; i++) {
    category[`cat_${i}`] = bodyData[`cat_${i}`] || "";
    category[`cat_${i}_hide`] = bodyData[`cat_${i}_hide`] === "true";
    category[`cat_${i}_itemsIds`] = bodyData[`cat_${i}_items`]
      ?.split(",")
      ?.filter((item: string) => item && allItemsIdsStringArray.includes(item)) || [];
    category[`cat_${i}_image`] = bodyData[`cat_${i}_image`] || "";
  }

  // Remove all category-related and UI-only keys from bodyData so they don't leak into the mutation
  Object.keys(bodyData).forEach((key) => {
    if (key.startsWith("cat_") || key === "category_count" || key === "menu_mode") {
      delete bodyData[key];
    }
  });
  try {
    const { data } = await client.mutate({
      mutation: CREATE_MENU,
      variables: {
        input: { ...bodyData, category, last_updated_by: user?.id },
      },
    });

    const mutationPromises = [];

    if (locationsIds.some((locationId) => locationId.trim() !== "")) {
      mutationPromises.push(
        client.mutate({
          mutation: CREATE_LOCATION_MENUS,
          variables: {
            objects: locationsIds
              .map((locationId) => ({
                location_id: locationId,
                menu_id: data.insert_menus_one.id,
              }))
              .filter((location) => location.location_id.trim() !== ""),
          },
        })
      );
    }

    if (itemsIds.some((itemId) => itemId.trim() !== "")) {
      mutationPromises.push(
        client.mutate({
          mutation: CREATE_MENU_ITEMS,
          variables: {
            objects: itemsIds
              .map((itemId) => ({
                item_id: itemId,
                menu_id: data.insert_menus_one.id,
              }))
              .filter((item) => item.item_id.trim() !== ""),
          },
        })
      );
    }

    // Execute all promises concurrently
    await Promise.all(mutationPromises);

    toast.success("Menu Created Successfully");
    return redirect("/menus/list");
  } catch (error) {
    toast.error("Error creating menu: " + error);
    return {};
  }
};

export const MenuCreate = () => {
  const { permissions } = usePermissions();
  const [selectedEvent, setSelectedEvent] = useState<string>();
  const [selectedVendor, setSelectedVendor] = useState<string>();

  const {
    items: allItems,
    vendors,
    locations,
    events,
    images,
  } = useLoaderData() as {
    items: Item[];
    vendors: Vendor[];
    locations: Location[];
    events: Event[];
    images: ImageLibrary[];
  };
  const imageOptions = images.map((item: { image_base64: string; display_name: string }) => ({
    value: item.image_base64,
    label: item.display_name,
    display_name: item.display_name,
    image_base64: item.image_base64,
  }));
  const organizationId = getState().organizationId;
  const [filteredLocations, setFilteredLocations] =
    useState<Location[]>(locations);
  const {
    data: itemsData,
    isLoading: itemsLoading,
    isFetching: itemsIsFetching,
    error: itemsError,
  } = useQuery<{
    itemsData: Item[];
  }>(
    [
      "table-data-menu-item-download",
      organizationId,
      selectedEvent,
      selectedVendor,
    ],
    async () => {
      let where: any = {};

      if (selectedEvent && selectedVendor) {
        if (organizationId) {
          where = {
            ...where,
            is_active: { _eq: true },
            event: {
              organization_id: { _eq: organizationId },
            },
          };
        }
        where = {
          ...where,
          is_active: { _eq: true },
          event_id: { _eq: selectedEvent },
          vendor_id: { _in: selectedVendor },
        };

        const { data, errors } = await client.query({
          query: QUERY_LIST_ITEMS,
          variables: {
            where,
            order_by: {
              name: "asc",
            },
          },
        });
        return {
          itemsData: data.items,
        };
      }
      return {
        itemsData: [],
      };
    }
  );
  const items = itemsData?.itemsData || [];
  const {
    data: itemsAllVendorsData,
    isLoading: itemsAllVendorsLoading,
    isFetching: itemsAllVendorsIsFetching,
    error: itemsAllVendorsError,
  } = useQuery<{
    itemsAllVendorsData: Item[];
  }>(
    [
      "table-data-menu-item-download-all-vendors",
      organizationId,
      selectedEvent,
    ],
    async () => {
      let where: any = {};

      if (selectedEvent) {
        if (organizationId) {
          where = {
            ...where,
            is_active: { _eq: true },
            event: {
              organization_id: { _eq: organizationId },
            },
          };
        }
        where = {
          ...where,
          is_active: { _eq: true },
          event_id: { _eq: selectedEvent },
          vendor_id: { _eq: "0" }, // Use "0" as the vendor_id
        };

        const { data, errors } = await client.query({
          query: QUERY_LIST_ITEMS,
          variables: {
            where,
            order_by: {
              name: "asc",
            },
          },
        });
        return {
          itemsAllVendorsData: data.items,
        };
      }
      return {
        itemsAllVendorsData: [],
      };
    }
  );
  const itemsAllVendors = itemsAllVendorsData?.itemsAllVendorsData || [];
  const itemIds = items
    .map((item) => item.id)
    .concat(itemsAllVendors.map((item) => item.id));

  // Dynamic categories state
  const [categories, setCategories] = useState<Category[]>(getDefaultCategories());

  const [menuMode, setMenuMode] = useState<string>("standard");
  const [tips, setTips] = useState(true);
  const [discounts, setDiscounts] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [isDiscountProtected, setIsDiscountProtected] = useState(false);
  const [isPrintingAuto, setIsPrintingAuto] = useState(false);
  const [isPrintingCopies, setIsPrintingCopies] = useState<number>(0);

  useEffect(() => {
    if (selectedEvent) {
      const updatedLocations = locations.filter(
        (location) => location.event_id.toString() === selectedEvent.toString()
      );
      setFilteredLocations(updatedLocations);
    } else {
      setFilteredLocations([]);
    }
  }, [selectedEvent, locations]);

  return (
    <Form method="post" action="/menus/create">
      <div className="py-4">
        <Breadcrumbs />
      </div>
      <FormLayout isTopSave={true}>
        <div className="col-span-5 sm:col-span-3">
          <Input label="Name" name="name" required />
        </div>
        <div className="col-span-1">
          <IndeterminateCheckbox
            label="Is Active?"
            name="is_active"
            defaultChecked
          />
        </div>
        <div className="col-span-6">
          <CustomSelect
            title="Event"
            name="event_id"
            required
            options={[
              ...(events?.map((e) => ({
                label: e.name,
                value: e.id,
              })) || []),
            ]}
            onChange={(e) => setSelectedEvent(e)}
          />
        </div>
        <div className="col-span-6">
          <CustomSelect
            title="Vendor"
            required
            name="vendor_id"
            options={[
              ...vendors.map((vendor) => ({
                label: vendor.name,
                value: vendor.id,
              })),
            ]}
            onChange={(e) => setSelectedVendor(e)}
          />
        </div>
        <div className="col-span-6">
          <CustomSelect
            title="Locations"
            name="locationsIds"
            options={[
              ...filteredLocations.map((location) => ({
                label: location.name,
                value: location.id,
              })),
            ]}
            multi
          />
        </div>
        <div className="col-span-6">
          <Tab
              labels={[
                "Payment Methods",
                "Tip Configuration",
                "Feature Configuration",
              ]}
          >
            <div className="grid grid-cols-6 gap-5">
              <div className="col-span-3 sm:col-span-1">
                <IndeterminateCheckbox
                    label="Cash?"
                    name="is_cash"
                    defaultChecked
                />
              </div>
              <div className="col-span-3 sm:col-span-1">
                <IndeterminateCheckbox
                    label="Credit?"
                    name="is_credit"
                    defaultChecked
                />
              </div>
              <div className="col-span-3 sm:col-span-1">
                <IndeterminateCheckbox
                    label="RFID?"
                    name="is_rfid"
                    defaultChecked
                />
              </div>
              <div className="col-span-3 sm:col-span-1">
                <IndeterminateCheckbox label="QR?" name="is_qr" />
              </div>
              <div className="col-span-3 sm:col-span-1">
                <IndeterminateCheckbox label="Tabs?" name="is_tab" />
              </div>
              <div className="col-span-6">
                <CustomSelect
                    title="Show Tax Line on Order Completion"
                    name="tax_type"
                    options={[
                      { label: "No", value: "inclusive" },
                      { label: "Yes", value: "exclusive" },
                    ]}
                    value="exclusive"
                />
              </div>
            </div>
            <div className="grid grid-cols-6 gap-5">
              <div className="col-span-6 sm:col-span-1">
                <IndeterminateCheckbox
                    label="Tips Accepted?"
                    name="is_tips"
                    defaultChecked={tips}
                    onChange={(checked) => setTips(checked as boolean)}
                />
              </div>
              {tips && (
                  <div className="col-span-2 sm:col-span-1">
                    <Input
                        label="Tip Percentage 1"
                        name="tip_percentage_1"
                        defaultValue={15}
                        required
                    />
                  </div>
              )}
              {tips && (
                  <div className="col-span-2 sm:col-span-1">
                    <Input
                        label="Tip Percentage 2"
                        name="tip_percentage_2"
                        defaultValue={18}
                        required
                    />
                  </div>
              )}
              {tips && (
                  <div className="col-span-2 sm:col-span-1">
                    <Input
                        label="Tip Percentage 3"
                        name="tip_percentage_3"
                        defaultValue={20}
                        required
                    />
                  </div>
              )}
            </div>
            <div className="grid grid-cols-6 gap-5">
              <div className="col-span-3 sm:col-span-1">
                <IndeterminateCheckbox
                    label="Custom Items Available?"
                    name="is_custom_item"
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <IndeterminateCheckbox
                    label="No Tax on Cash?"
                    name="is_cash_not_taxed"
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <IndeterminateCheckbox
                    label="Box Office Sales?"
                    name="box_office_sales"
                />
              </div>
              <div
                  className={discounts ? "col-span-3 sm:col-span-1" : "col-span-2"}
              >
                <IndeterminateCheckbox
                    label="Discounts Available?"
                    name="is_discount"
                    defaultChecked={discounts}
                    onChange={(checked) => {
                      setDiscounts(checked as boolean);
                      if (!checked) setIsDiscountProtected(false);
                    }}
                />
              </div>
              {discounts && (
                  <div className="col-span-3 sm:col-span-1">
                    <IndeterminateCheckbox
                        label="Discounts Password Protected?"
                        name="is_discount_protected"
                        defaultChecked={isDiscountProtected}
                        onChange={(checked) =>
                            setIsDiscountProtected(checked as boolean)
                        }
                        required
                    />
                  </div>
              )}
              <div className={printing ? "col-span-3 sm:col-span-1" : "col-span-6"}>
                <IndeterminateCheckbox
                    label="Ticket Printing Enabled?"
                    name="is_printing"
                    defaultChecked={printing}
                    onChange={(checked) => {
                      setPrinting(checked as boolean);
                      if (!checked) setIsPrintingAuto(false);
                      if (!checked) setIsPrintingCopies(0);
                    }}
                />
              </div>
              {printing && (
                  <div
                      className={
                        isPrintingAuto ? "col-span-3 sm:col-span-1" : "col-span-3"
                      }
                  >
                    <IndeterminateCheckbox
                        label="Auto Print Tickets on Order Completion?"
                        name="is_printing_auto"
                        defaultChecked={isPrintingAuto}
                        onChange={(checked) => {
                          setIsPrintingAuto(checked as boolean);
                          if (!checked) setIsPrintingCopies(0);
                        }}
                    />
                  </div>
              )}
              {isPrintingAuto && (
                  <div className="col-span-6 sm:col-span-4">
                    <Input
                        label="Ticket Copies to Automatically Print?"
                        name="is_printing_copies"
                        defaultValue={isPrintingCopies}
                        onChange={(value) =>
                            setIsPrintingCopies(typeof value === "number" ? value : 0)
                        }
                    />
                  </div>
              )}
              <div className="col-span-3 sm:col-span-2">
                <CustomSelect
                    title="Menu Mode"
                    name="menu_mode"
                    defaultValue="standard"
                    options={[
                      { label: "Standard", value: "standard" },
                      { label: "Scan To Cart", value: "scan_to_cart" },
                      { label: "Category", value: "category" },
                    ]}
                    onChange={(e) => setMenuMode(e)}
                />
                <input type="hidden" name="is_cart_scanning" value={String(menuMode === "scan_to_cart")} />
                <input type="hidden" name="is_category_mode" value={String(menuMode === "category")} />
              </div>
              {/*<div className="col-span-6">*/}
              {/*  <CustomSelect*/}
              {/*      title="Default Processing Mode"*/}
              {/*      name="processing_mode"*/}
              {/*      required*/}
              {/*      defaultValue="Dynamic Processing"*/}
              {/*      options={[*/}
              {/*        {value: "Dynamic Processing", label: "Dynamic Processing"},*/}
              {/*        {value: "Online Only (No Batches)", label: "Online Only (No Batches)"},*/}
              {/*        {value: "Offline Mode (No Real-Time Auth)", label: "Offline Mode (No Real-Time Auth)"},*/}
              {/*      ]}*/}
              {/*  />*/}
              {/*</div>*/}
            </div>
          </Tab>
        </div>
        <div className="col-span-6">
          <CategoryManager
            categories={categories}
            setCategories={setCategories}
            items={items}
            itemsAllVendors={itemsAllVendors}
            selectedEvent={selectedEvent}
            selectedVendor={selectedVendor}
            imageOptions={imageOptions}
          />
        </div>
        <input type="hidden" value={JSON.stringify(itemIds)} name="all_items" />
      </FormLayout>
    </Form>
  );
};
